import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import EyeIcon from "../../assets/icons/BlueEyeDisabled.svg";

import { InputProps } from "./Input.types";
import {
    InputLabel,
    InputPrefixWrapper,
    InputSuffixWrapper,
    InputWrapper,
    StyledInput,
} from "./Input.styles";
import Typography from "../Typography";
import Flex from "../../layout/Flex";
import { isValidValue } from "../../methods/common";
import COLORS from "../../constants/colors";
import InputHelperLabel from "../common/InputHelperLabel/InputHelperLabel";

const getAdditionalPropsBasedOnType = (props: InputProps) => {
    let LeftNode;
    let RightNode;
    let countMsg;
    let maxLength;
    let maxValue;
    let minValue;
    let step;

    switch (props.type) {
        case "text":
            {
                const {
                    prefixNode,
                    suffixNode,
                    showCharacterCount,
                    value,
                    length = 100,
                } = props;
                LeftNode = prefixNode;
                RightNode = suffixNode;
                maxLength = length;

                countMsg = showCharacterCount
                    ? `${value?.length ?? 0} / ${length}`
                    : "";
            }
            break;

        case "number":
            {
                const { prefixNode, suffixNode, max, min } = props;

                LeftNode = prefixNode;
                RightNode = suffixNode;
                maxValue = max;
                minValue = min;
                step = props?.step;

                countMsg = `${isValidValue(min) ? `min: ${min}` : ""} ${
                    isValidValue(max) ? `max: ${max}` : ""
                }`.trim();
            }
            break;

        case "password":
            {
                const { showCharacterCount, value, length = 100 } = props;
                RightNode = <EyeIcon />;
                maxLength = length;

                countMsg = showCharacterCount
                    ? `${value?.length ?? 0} / ${length}`
                    : "";
            }
            break;

        default:
            break;
    }

    return {
        LeftNode,
        RightNode,
        countMsg,
        maxLength,
        minValue,
        maxValue,
        step,
    };
};

const Input = forwardRef<HTMLDivElement, InputProps>(
    (props: InputProps, ref) => {
        const {
            type,
            name,
            label,
            headingLabel,
            headingLabelStyleProps,
            value = "",
            onChange,
            onBlur,
            outline = "border",
            disabled = false,
            required = false,
            helpMsg,
            errorMsg,
            onFocus,
            onPaste,
            autoFocus = false,
            id,
            placeholder,
            state,
            inputMode,
            enableDebounce,
            debounceInterval = 500,
            debounceValidationFunction,
            borderSize,
            weight,
            readOnly,
        } = props;

        const inputRef = useRef<HTMLInputElement>(null);
        useImperativeHandle(ref, () => inputRef.current!);

        const {
            LeftNode,
            RightNode,
            countMsg,
            maxLength,
            maxValue,
            minValue,
            step,
        } = getAdditionalPropsBasedOnType(props);

        const handleTogglePassword = () => {
            if (type === "password") {
                const isPassword = inputRef.current!.type === "password";
                inputRef.current!.type = isPassword ? "text" : "password";
            }
        };

        const onClickLeftNode = () => {};
        const onClickRightNode =
            type === "password" ? handleTogglePassword : () => {};

        /**
         * Debounce functionality start
         */
        const [inputValue, setInputValue] = useState("");
        const [timerId, setTimerId] = useState<NodeJS.Timeout>();

        const debounce = (value: any, delay: number) => {
            if (timerId) clearTimeout(timerId);
            const TimeoutId = setTimeout(() => {
                onChange(value);
            }, delay);

            setTimerId(TimeoutId);
        };

        useEffect(() => {
            if (inputValue !== "" && enableDebounce)
                debounce(inputValue, debounceInterval);
        }, [inputValue, enableDebounce]);

        /**
         * The below useEffect is added to clear the input field with this scenario
         * When debounce is enabled this component will use inner state for input field and then passes it to outside
         * so even if the value is change from outside state it'll not reflect in UI since the inner state is not cleared or modified
         * so the below condition will work if the outside value is cleared from outside
         */
        useEffect(() => {
            if (enableDebounce && inputValue !== "" && !Boolean(value)) {
                setInputValue("");
            }
        }, [value]);

        const _onChange = (e: any) => {
            if (enableDebounce && e.target.value === "") {
                setInputValue("");
                onChange(e.target.value);
            } else if (enableDebounce) {
                setInputValue(
                    debounceValidationFunction
                        ? debounceValidationFunction?.(e.target.value)
                        : e.target.value,
                );
            } else onChange(e);
        };

        /**
         * Debounce functionality end
         */

        return (
            <Flex flexDirection="column" gap={10}>
                <Typography
                    data-input-heading-label
                    color={COLORS.GREY_900}
                    required={required}
                    {...headingLabelStyleProps}
                >
                    {headingLabel}
                </Typography>
                <InputWrapper
                    outline={outline}
                    filled={isValidValue(value)}
                    disabled={disabled}
                    state={state}
                    data-input-wrapper
                    borderSize={borderSize}
                >
                    {LeftNode ? (
                        <InputPrefixWrapper
                            children={LeftNode}
                            onClick={onClickLeftNode}
                            data-input-affix
                            data-input-prefix
                        />
                    ) : null}
                    <Flex flex={1} flexDirection="column">
                        {label ? (
                            <InputLabel>
                                <Typography required={required}>
                                    {label}
                                </Typography>
                            </InputLabel>
                        ) : null}

                        <StyledInput
                            // type={type}
                            placeholder={placeholder}
                            name={name}
                            id={id}
                            label={label}
                            ref={inputRef}
                            disabled={disabled}
                            required={required}
                            value={
                                enableDebounce
                                    ? String(inputValue)
                                    : String(value)
                            }
                            maxLength={maxLength}
                            autoFocus={autoFocus}
                            onChange={_onChange}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            max={maxValue}
                            min={minValue}
                            onPaste={onPaste}
                            autoComplete="off"
                            inputMode={inputMode}
                            weight={weight}
                            readOnly={readOnly}
                            step={step}
                            onWheel={(e) =>
                                e.target instanceof HTMLElement &&
                                e.target.blur()
                            }
                            {...(props?.__experimental__props as any)}
                        />
                    </Flex>
                    {RightNode ? (
                        <InputSuffixWrapper
                            children={RightNode}
                            onClick={onClickRightNode}
                            data-input-affix
                            data-input-suffix
                        />
                    ) : null}
                </InputWrapper>
                {helpMsg || errorMsg || countMsg ? (
                    <Flex justifyContent="space-between">
                        <Flex flexDirection="column" gap={10}>
                            {helpMsg ? (
                                <InputHelperLabel type="desc">
                                    {helpMsg}
                                </InputHelperLabel>
                            ) : (
                                <></>
                            )}
                            {errorMsg ? (
                                <InputHelperLabel
                                    type="error"
                                    id={id + "_error"}
                                >
                                    {errorMsg}
                                </InputHelperLabel>
                            ) : (
                                <></>
                            )}
                        </Flex>
                        <InputHelperLabel type="desc">
                            {countMsg}
                        </InputHelperLabel>
                    </Flex>
                ) : null}
            </Flex>
        );
    },
);

export default Input;
